// frontend/src/components/PlayerPerformance.jsx
import { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Target,
  BarChart3,
  Loader2,
  ChevronDown,
  ChevronUp,
  DollarSign,
} from 'lucide-react';
import PlayerDistribution from './PlayerDistribution';
import PlayerQuadrant from './PlayerQuadrant';
import ReferralStrip from './shared/ReferralStrip';
import AdSlot from './shared/AdSlot';
import {
  getRecentGameLogs,
  getSeasonAverages,
  computeRollingAverage,
  getCurrentSeason,
} from '../services/nbaApi';

// ─── Stat windows to compare ────────────────────────────────────────────────
const WINDOWS = [5, 10, 20];
const STAT_KEYS = [
  { key: 'pts', label: 'PTS', color: 'orange' },
  { key: 'reb', label: 'REB', color: 'blue' },
  { key: 'ast', label: 'AST', color: 'green' },
  { key: 'fg_pct', label: 'FG%', color: 'purple', isSuffix: '%' },
  { key: 'fg3_pct', label: '3P%', color: 'yellow', isSuffix: '%' },
  { key: 'ft_pct', label: 'FT%', color: 'pink', isSuffix: '%' },
  { key: 'stl', label: 'STL', color: 'cyan' },
  { key: 'blk', label: 'BLK', color: 'red' },
  { key: 'turnover', label: 'TO', color: 'gray', invertTrend: true },
  { key: 'min', label: 'MIN', color: 'slate' },
];


// Static NBA team ID → abbreviation map (IDs from BallDontLie API)
const TEAM_ABBR = {
  1:'ATL', 2:'BOS', 3:'BKN', 4:'CHA', 5:'CHI', 6:'CLE', 7:'DAL', 8:'DEN',
  9:'DET', 10:'GSW', 11:'HOU', 12:'IND', 13:'LAC', 14:'LAL', 15:'MEM',
  16:'MIA', 17:'MIL', 18:'MIN', 19:'NOP', 20:'NYK', 21:'OKC', 22:'ORL',
  23:'PHI', 24:'PHX', 25:'POR', 26:'SAC', 27:'SAS', 28:'TOR', 29:'UTA', 30:'WAS',
};

const colorMap = {
  orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', stroke: '#f97316' },
  blue:   { text: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/30',   stroke: '#60a5fa' },
  green:  { text: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/30',  stroke: '#4ade80' },
  purple: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', stroke: '#c084fc' },
  yellow: { text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', stroke: '#facc15' },
  pink:   { text: 'text-pink-400',   bg: 'bg-pink-500/10',   border: 'border-pink-500/30',   stroke: '#f472b6' },
  cyan:   { text: 'text-cyan-400',   bg: 'bg-cyan-500/10',   border: 'border-cyan-500/30',   stroke: '#22d3ee' },
  red:    { text: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/30',    stroke: '#f87171' },
  gray:   { text: 'text-gray-400',   bg: 'bg-gray-500/10',   border: 'border-gray-500/30',   stroke: '#9ca3af' },
  slate:  { text: 'text-slate-400',  bg: 'bg-slate-500/10',  border: 'border-slate-500/30',  stroke: '#94a3b8' },
};

// ─── Linear regression helper ────────────────────────────────────────────────
// Returns { slope, intercept, r2 } for y = slope * x + intercept
function linearRegression(values) {
  const n = values.length;
  if (n < 2) return { slope: 0, intercept: values[0] ?? 0, r2: 0 };
  const xs = values.map((_, i) => i);
  const sumX = xs.reduce((s, x) => s + x, 0);
  const sumY = values.reduce((s, y) => s + y, 0);
  const sumXY = xs.reduce((s, x, i) => s + x * values[i], 0);
  const sumX2 = xs.reduce((s, x) => s + x * x, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  const meanY = sumY / n;
  const ssTot = values.reduce((s, y) => s + (y - meanY) ** 2, 0);
  const ssRes = values.reduce((s, y, i) => s + (y - (slope * i + intercept)) ** 2, 0);
  const r2 = ssTot === 0 ? 0 : 1 - ssRes / ssTot;

  return { slope, intercept, r2 };
}

// Threshold: slope normalised by mean to be "highly" trending
const HIGH_TREND_THRESHOLD = 0.04; // 4% per-game change relative to mean

function getTrendEmoji(gameLogs) {
  // Use PTS as the primary indicator (most visible stat)
  if (!gameLogs || gameLogs.length < 5) return null;
  const values = [...gameLogs].reverse().map((g) => Number(g.pts) || 0);
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  if (mean === 0) return null;
  const { slope } = linearRegression(values);
  const normSlope = slope / mean;
  if (normSlope >= HIGH_TREND_THRESHOLD) return '🔥';
  if (normSlope <= -HIGH_TREND_THRESHOLD) return '🧊';
  return null;
}

// ─── Scatter plot with line of best fit (pure SVG, no library needed) ────────
function ScatterPlot({ values, color, label, suffix = '', window: w }) {
  const W = 280, H = 140, PAD = { top: 12, right: 12, bottom: 28, left: 36 };

  const data = values.slice(0, w).reverse(); // oldest → newest (left → right)
  if (data.length < 2) return null;

  const minY = Math.min(...data) * 0.9;
  const maxY = Math.max(...data) * 1.1 || 1;
  const rangeY = maxY - minY || 1;

  const toX = (i) => PAD.left + (i / (data.length - 1)) * (W - PAD.left - PAD.right);
  const toY = (v) => PAD.top + (1 - (v - minY) / rangeY) * (H - PAD.top - PAD.bottom);

  const { slope, intercept } = linearRegression(data);
  const lobfY0 = intercept;
  const lobfY1 = slope * (data.length - 1) + intercept;

  const stroke = colorMap[color]?.stroke ?? '#f97316';

  // Y-axis ticks (3 labels)
  const yTicks = [minY, (minY + maxY) / 2, maxY];

  return (
    <div className={`${colorMap[color].bg} border ${colorMap[color].border} rounded-xl p-3`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-semibold ${colorMap[color].text}`}>{label}</span>
        <span className="text-xs text-gray-500">Last {w} games</span>
      </div>
      <svg width={W} height={H} className="w-full" viewBox={`0 0 ${W} ${H}`}>
        {/* Y-axis gridlines + labels */}
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={PAD.left} x2={W - PAD.right}
              y1={toY(tick)} y2={toY(tick)}
              stroke="#374151" strokeWidth="1" strokeDasharray="3 3"
            />
            <text
              x={PAD.left - 4} y={toY(tick) + 4}
              textAnchor="end" fontSize="9" fill="#6b7280"
            >
              {Number.isInteger(tick) ? tick : tick.toFixed(1)}{suffix}
            </text>
          </g>
        ))}

        {/* X-axis game labels */}
        {data.map((_, i) => (
          i % Math.ceil(data.length / 5) === 0 && (
            <text
              key={i}
              x={toX(i)} y={H - 4}
              textAnchor="middle" fontSize="9" fill="#6b7280"
            >
              G{i + 1}
            </text>
          )
        ))}

        {/* Scatter dots */}
        {data.map((v, i) => (
          <circle
            key={i}
            cx={toX(i)} cy={toY(v)}
            r={3.5}
            fill={stroke}
            opacity={0.75}
          />
        ))}

        {/* Line of best fit */}
        <line
          x1={toX(0)} y1={toY(lobfY0)}
          x2={toX(data.length - 1)} y2={toY(lobfY1)}
          stroke={stroke} strokeWidth="2" strokeDasharray="5 3"
          opacity={0.9}
        />
      </svg>
    </div>
  );
}

// ─── Trend icon helper ──────────────────────────────────────────────────────
function TrendIcon({ value, baseline, invertTrend = false }) {
  const diff = parseFloat(value) - parseFloat(baseline);
  const threshold = 0.5;
  const isUp = diff > threshold;
  const isDown = diff < -threshold;
  const positive = invertTrend ? isDown : isUp;
  const negative = invertTrend ? isUp : isDown;
  if (positive) return <TrendingUp className="inline h-4 w-4 text-green-400 ml-1" />;
  if (negative) return <TrendingDown className="inline h-4 w-4 text-red-400 ml-1" />;
  return <Minus className="inline h-4 w-4 text-gray-500 ml-1" />;
}

// ─── Main Component ─────────────────────────────────────────────────────────

const PlayerPerformance = ({ player }) => {
  const [gameLogs, setGameLogs] = useState([]);
  const [seasonAvg, setSeasonAvg] = useState(null);
  const [rollingAvgs, setRollingAvgs] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showGameLog, setShowGameLog] = useState(false);
  const [activeWindow, setActiveWindow] = useState(10);
  const [scatterWindow, setScatterWindow] = useState(20);
  const [salaryData, setSalaryData] = useState(null);

  const season = getCurrentSeason();

  // Load salary data
  useEffect(() => {
    fetch('/nba-salaries-2025-26.json')
      .then((res) => res.json())
      .then((data) => setSalaryData(data))
      .catch((err) => console.error('Failed to load salary data:', err));
  }, []);

  const fetchData = async (cancelled) => {
    const [logs, avgRes] = await Promise.all([
      getRecentGameLogs(player.id, season, 100),
      getSeasonAverages(player.id, season).catch((err) => {
        if (err.status === 401) return null;
        throw err;
      }),
    ]);

    if (cancelled?.value) return;

    setGameLogs(logs);

    const avgData = avgRes?.data?.[0] || null;
    if (avgData) {
      setSeasonAvg({
        pts:      avgData.pts?.toFixed(1) ?? '0.0',
        reb:      avgData.reb?.toFixed(1) ?? '0.0',
        ast:      avgData.ast?.toFixed(1) ?? '0.0',
        stl:      avgData.stl?.toFixed(1) ?? '0.0',
        blk:      avgData.blk?.toFixed(1) ?? '0.0',
        fg_pct:   avgData.fg_pct  != null ? (avgData.fg_pct  * 100).toFixed(1) : '0.0',
        fg3_pct:  avgData.fg3_pct != null ? (avgData.fg3_pct * 100).toFixed(1) : '0.0',
        ft_pct:   avgData.ft_pct  != null ? (avgData.ft_pct  * 100).toFixed(1) : '0.0',
        turnover: avgData.turnover?.toFixed(1) ?? '0.0',
        min:      avgData.min != null ? parseFloat(avgData.min).toFixed(1) : '0.0',
        games_played: avgData.games_played ?? 0,
        // raw FG / 3P / FT for game log season avg row
        fgm: avgData.fgm?.toFixed(1) ?? '0.0',
        fga: avgData.fga?.toFixed(1) ?? '0.0',
        fg3m: avgData.fg3m?.toFixed(1) ?? '0.0',
        fg3a: avgData.fg3a?.toFixed(1) ?? '0.0',
      });
    } else {
      setSeasonAvg(null);
    }

    const rolling = {};
    WINDOWS.forEach((w) => { rolling[w] = computeRollingAverage(logs, w); });
    setRollingAvgs(rolling);
  };

  useEffect(() => {
    if (!player?.id) return;
    const cancelled = { value: false };
    setLoading(true);
    setError(null);

    fetchData(cancelled)
      .catch((err) => {
        if (!cancelled.value) {
          console.error('Failed to load player performance:', err);
          setError(err.message || 'Failed to load stats');
        }
      })
      .finally(() => { if (!cancelled.value) setLoading(false); });

    return () => { cancelled.value = true; };
  }, [player?.id, season]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Trend emoji based on PTS slope over all available games ──────────────
  const trendEmoji = useMemo(() => getTrendEmoji(gameLogs), [gameLogs]);

  // ─── Salary info and percentile ─────────────────────────────────────────────
  const salaryInfo = useMemo(() => {
    if (!salaryData || !player) return null;

    const salaries = salaryData.salaries || [];
    const playerFullName = `${player.first_name} ${player.last_name}`.toLowerCase();
    const playerSalary = salaries.find(s => s.player.toLowerCase() === playerFullName);

    if (!playerSalary) return null;

    // Calculate percentile
    const sorted = salaries.map(s => s.salary).sort((a, b) => a - b);
    const rank = sorted.filter(sal => sal <= playerSalary.salary).length;
    const percentile = Math.round((rank / sorted.length) * 100);

    return {
      salary: playerSalary.salary,
      percentile
    };
  }, [salaryData, player]);

  // ─── Per-stat values for scatter plots (oldest→newest already handled inside) ─
  const scatterData = useMemo(() => {
    if (!gameLogs.length) return {};
    // gameLogs is newest-first; we'll reverse inside ScatterPlot
    return {
      pts:     gameLogs.map((g) => Number(g.pts)      || 0),
      reb:     gameLogs.map((g) => Number(g.reb)      || 0),
      ast:     gameLogs.map((g) => Number(g.ast)      || 0),
      stl:     gameLogs.map((g) => Number(g.stl)      || 0),
      blk:     gameLogs.map((g) => Number(g.blk)      || 0),
      fg_pct:  gameLogs.map((g) => g.fga > 0 ? (g.fgm / g.fga) * 100 : 0),
      fg3_pct: gameLogs.map((g) => g.fg3a > 0 ? (g.fg3m / g.fg3a) * 100 : 0),
      ft_pct:  gameLogs.map((g) => g.fta > 0 ? (g.ftm / g.fta) * 100 : 0),
      turnover: gameLogs.map((g) => Number(g.turnover) || 0),
      min:     gameLogs.map((g) => parseFloat(g.min)  || 0),
    };
  }, [gameLogs]);

  if (!player) return null;

  const playerName = `${player.first_name} ${player.last_name}`;
  const teamAbbr   = player.team?.abbreviation || '';
  const position   = player.position || '';

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-2xl p-12 border border-gray-700/50 text-center">
        <Loader2 className="h-10 w-10 text-orange-500 mx-auto mb-4 animate-spin" />
        <p className="text-gray-300">Loading stats for {playerName}…</p>
      </div>
    );
  }

  // ─── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="bg-red-900/20 rounded-2xl p-8 border border-red-700/50 text-center">
        <p className="text-red-300 font-medium mb-2">Error loading stats</p>
        <p className="text-red-400/70 text-sm mb-4">{error}</p>
        <button
          onClick={() => {
            setError(null);
            setLoading(true);
            const cancelled = { value: false };
            fetchData(cancelled)
              .catch((e) => setError(e.message || 'Failed to load stats'))
              .finally(() => setLoading(false));
          }}
          className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const activeAvg = activeWindow === 'season' ? seasonAvg : rollingAvgs[activeWindow];

  return (
    <div className="space-y-6">
      {/* ── Player Header ──────────────────────────────────────────────── */}
      <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-2xl font-bold text-white">
              {playerName}
              {trendEmoji && <span className="ml-2 text-2xl">{trendEmoji}</span>}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {teamAbbr && (
                <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded text-sm font-medium">
                  {teamAbbr}
                </span>
              )}
              {position && (
                <span className="bg-gray-700 text-gray-300 px-2 py-0.5 rounded text-sm">
                  {position}
                </span>
              )}
              {salaryInfo && (
                <>
                  <div className="flex items-center gap-1.5 bg-gray-700/50 px-2 py-0.5 rounded text-sm">
                    <DollarSign className="h-4 w-4 text-green-400" />
                    <span className="font-mono text-gray-200">
                      {(salaryInfo.salary / 1000000).toFixed(1)}M
                    </span>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded font-semibold ${
                      salaryInfo.percentile >= 90
                        ? 'bg-green-900/50 text-green-300'
                        : salaryInfo.percentile >= 70
                        ? 'bg-blue-900/50 text-blue-300'
                        : salaryInfo.percentile >= 50
                        ? 'bg-yellow-900/50 text-yellow-300'
                        : 'bg-gray-700/50 text-gray-400'
                    }`}
                  >
                    {salaryInfo.percentile}th %ile
                  </span>
                </>
              )}
              {seasonAvg && (
                <span className="text-gray-500 text-sm">
                  {seasonAvg.games_played} games played ({season}-{(season + 1).toString().slice(2)})
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Season Averages summary ─────────────────────────────────── */}
        {seasonAvg && (
          <div>
            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Full Season Averages
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {STAT_KEYS.slice(0, 5).map(({ key, label, color }) => (
                <div key={key} className="text-center">
                  <div className={`text-2xl font-bold ${colorMap[color].text}`}>
                    {seasonAvg[key]}{key.includes('pct') ? '%' : ''}
                  </div>
                  <div className="text-gray-400 text-sm">{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Referral strip — update hrefs in src/config/referrals.js */}
      <ReferralStrip variant="strip" className="py-1" />

      {/* ── Rolling Average Comparison ─────────────────────────────────── */}
      <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Activity className="h-6 w-6 text-orange-500" />
            <h4 className="text-xl font-bold text-white">Performance Trends</h4>
          </div>
          <div className="flex gap-2 flex-wrap">
            {WINDOWS.map((w) => (
              <button
                key={w}
                onClick={() => setActiveWindow(w)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  activeWindow === w
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                }`}
              >
                Last {w}
              </button>
            ))}
            {seasonAvg && (
              <button
                onClick={() => setActiveWindow('season')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  activeWindow === 'season'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                }`}
              >
                Full Season
              </button>
            )}
          </div>
        </div>

        {activeAvg ? (
          <div className="space-y-6">
            {/* Stat comparison grid */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {STAT_KEYS.map(({ key, label, color, invertTrend }) => {
                const recentVal = activeAvg[key];
                const seasonVal = seasonAvg?.[key] || '0.0';
                const isSeasonView = activeWindow === 'season';
                return (
                  <div key={key} className={`${colorMap[color].bg} border ${colorMap[color].border} rounded-xl p-3 text-center`}>
                    <div className={`text-lg font-bold ${colorMap[color].text}`}>
                      {recentVal}{key.includes('pct') ? '%' : ''}
                      {!isSeasonView && seasonAvg && (
                        <TrendIcon value={recentVal} baseline={seasonVal} invertTrend={invertTrend} />
                      )}
                    </div>
                    <div className="text-gray-400 text-xs mt-0.5">{label}</div>
                    {!isSeasonView && seasonAvg && (
                      <div className="text-gray-500 text-xs mt-1">
                        Season: {seasonVal}{key.includes('pct') ? '%' : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Comparison table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-600/50">
                    <th className="text-left py-2 text-gray-400 font-medium">Span</th>
                    {STAT_KEYS.slice(0, 8).map(({ key, label, color }) => (
                      <th key={key} className={`text-center py-2 ${colorMap[color].text} font-medium`}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {WINDOWS.map((w) => {
                    const avg = rollingAvgs[w];
                    if (!avg) return null;
                    return (
                      <tr key={w} className={`border-b border-gray-700/30 ${w === activeWindow ? 'bg-orange-500/5' : ''}`}>
                        <td className="py-2 text-gray-300 font-medium">
                          Last {w} <span className="text-gray-500 text-xs">({avg.games}g)</span>
                        </td>
                        {STAT_KEYS.slice(0, 8).map(({ key, color }) => (
                          <td key={key} className={`text-center py-2 ${colorMap[color].text}`}>
                            {avg[key]}{key.includes('pct') ? '%' : ''}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {seasonAvg && (
                    <tr className={`border-t border-gray-600/50 ${activeWindow === 'season' ? 'bg-orange-500/5' : 'bg-gray-700/20'}`}>
                      <td className="py-2 text-white font-semibold">
                        Season <span className="text-gray-500 text-xs">({seasonAvg.games_played}g)</span>
                      </td>
                      {STAT_KEYS.slice(0, 8).map(({ key, color }) => (
                        <td key={key} className={`text-center py-2 font-semibold ${colorMap[color].text}`}>
                          {seasonAvg[key]}{key.includes('pct') ? '%' : ''}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-gray-400 text-center py-6">
            No game log data available for the {season}-{(season + 1).toString().slice(2)} season.
          </p>
        )}
      </div>

      {/* ── Insight Cards ──────────────────────────────────────────────── */}
      {activeAvg && seasonAvg && (
        <div className="grid md:grid-cols-3 gap-4">
          <InsightCard icon={TrendingUp} title="Scoring Trend"  color="orange" recent={activeAvg.pts}    season={seasonAvg.pts}    label="PPG"  window={activeWindow} />
          <InsightCard icon={Target}     title="Efficiency"     color="green"  recent={activeAvg.fg_pct} season={seasonAvg.fg_pct} label="FG%" suffix="%" window={activeWindow} />
          <InsightCard icon={BarChart3}  title="Playmaking"     color="blue"   recent={activeAvg.ast}    season={seasonAvg.ast}    label="APG"  window={activeWindow} />
        </div>
      )}

      {/* Ad slot — rectangle between stats and charts; only renders when configured */}
      <AdSlot slot="rectangle" />

      {/* ── Distribution Analysis (Bell Curves) ───────────────────────── */}
      {gameLogs.length >= 3 && (
        <PlayerDistribution
          player={player}
          gameLogs={gameLogs.filter((g) => parseInt(g.min) > 0)}
          seasonAvg={seasonAvg}
        />
      )}

      {/* ── Scatter Plots ──────────────────────────────────────────────── */}
      {gameLogs.length >= 3 && (
        <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-6 w-6 text-orange-500" />
              <h4 className="text-xl font-bold text-white">Trend Charts</h4>
            </div>
            <div className="flex gap-2">
              {WINDOWS.map((w) => (
                <button
                  key={w}
                  onClick={() => setScatterWindow(w)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    scatterWindow === w
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                  }`}
                >
                  Last {w}
                </button>
              ))}
            </div>
          </div>
          <p className="text-gray-500 text-xs mb-4">
            Dashed line = line of best fit. Dots = individual games (most recent on right).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {STAT_KEYS.map(({ key, label, color, isSuffix }) => (
              scatterData[key]?.length >= 2 && (
                <ScatterPlot
                  key={key}
                  values={scatterData[key]}
                  color={color}
                  label={label}
                  suffix={isSuffix || ''}
                  window={Math.min(scatterWindow, scatterData[key].length)}
                />
              )
            ))}
          </div>
        </div>
      )}

      {/* ── Player Profile Quadrant ────────────────────────────────────── */}
      {gameLogs.length >= 5 && (
        <PlayerQuadrant player={player} gameLogs={gameLogs} />
      )}

      {/* ── Betting Analytics ──────────────────────────────────────────── */}
      {gameLogs.length >= 5 && (
        <BettingAnalytics gameLogs={gameLogs} playerName={playerName} />
      )}

      {/* ── Expandable Game Log ────────────────────────────────────────── */}
      {gameLogs.length > 0 && (
        <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden">
          <button
            onClick={() => setShowGameLog(!showGameLog)}
            className="w-full flex items-center justify-between px-6 py-4 text-white hover:bg-gray-700/30 transition-colors"
          >
            <h4 className="text-lg font-bold">Game Log ({gameLogs.length} games)</h4>
            {showGameLog
              ? <ChevronUp className="h-5 w-5 text-gray-400" />
              : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </button>

          {showGameLog && (
            <div className="px-6 pb-6 overflow-x-auto">
              <table className="w-full text-sm text-white">
                <thead>
                  <tr className="border-b border-gray-600/50">
                    <th className="text-left py-2 text-gray-400">Date</th>
                    <th className="text-left py-2 text-gray-400">vs</th>
                    <th className="text-center py-2 text-gray-400">MIN</th>
                    <th className="text-center py-2 text-orange-400">PTS</th>
                    <th className="text-center py-2 text-blue-400">REB</th>
                    <th className="text-center py-2 text-green-400">AST</th>
                    <th className="text-center py-2 text-cyan-400">STL</th>
                    <th className="text-center py-2 text-red-400">BLK</th>
                    <th className="text-center py-2 text-purple-400">FG</th>
                    <th className="text-center py-2 text-yellow-400">3P</th>
                    <th className="text-center py-2 text-gray-400">TO</th>
                  </tr>
                </thead>
                <tbody>
                  {gameLogs.map((game, idx) => {
                    const date = game.game?.date
                      ? new Date(game.game.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '—';
                    const isHome = game.game?.home_team_id === game.team?.id;
                    const opponentId = isHome ? game.game?.visitor_team_id : game.game?.home_team_id;
                    const opponentAbbr = TEAM_ABBR[opponentId] || '?';
                    const opponent = isHome ? `vs ${opponentAbbr}` : `@ ${opponentAbbr}`;

                    return (
                      <tr key={idx} className="border-b border-gray-700/30 hover:bg-gray-700/20 transition-colors">
                        <td className="py-2 text-gray-300 whitespace-nowrap">{date}</td>
                        <td className="py-2 text-gray-300 whitespace-nowrap">{opponent}</td>
                        <td className="text-center py-2 text-gray-300">
                          {parseInt(game.min) > 0 ? game.min : <span className="text-gray-600 text-xs">DNP</span>}
                        </td>
                        <td className="text-center py-2 text-orange-400 font-semibold">{game.pts}</td>
                        <td className="text-center py-2 text-blue-400">{game.reb}</td>
                        <td className="text-center py-2 text-green-400">{game.ast}</td>
                        <td className="text-center py-2 text-cyan-400">{game.stl}</td>
                        <td className="text-center py-2 text-red-400">{game.blk}</td>
                        <td className="text-center py-2 text-purple-400">{game.fgm}/{game.fga}</td>
                        <td className="text-center py-2 text-yellow-400">{game.fg3m}/{game.fg3a}</td>
                        <td className="text-center py-2 text-gray-400">{game.turnover}</td>
                      </tr>
                    );
                  })}

                  {/* ── Season Averages row ─────────────────────────────── */}
                  {seasonAvg && (
                    <tr className="border-t-2 border-orange-500/40 bg-orange-500/5 font-semibold">
                      <td className="py-2 text-orange-300 whitespace-nowrap">Season Avg</td>
                      <td className="py-2 text-gray-500 text-xs">{seasonAvg.games_played}g</td>
                      <td className="text-center py-2 text-gray-300">{seasonAvg.min}</td>
                      <td className="text-center py-2 text-orange-400">{seasonAvg.pts}</td>
                      <td className="text-center py-2 text-blue-400">{seasonAvg.reb}</td>
                      <td className="text-center py-2 text-green-400">{seasonAvg.ast}</td>
                      <td className="text-center py-2 text-cyan-400">{seasonAvg.stl}</td>
                      <td className="text-center py-2 text-red-400">{seasonAvg.blk}</td>
                      <td className="text-center py-2 text-purple-400">{seasonAvg.fgm}/{seasonAvg.fga}</td>
                      <td className="text-center py-2 text-yellow-400">{seasonAvg.fg3m}/{seasonAvg.fg3a}</td>
                      <td className="text-center py-2 text-gray-400">{seasonAvg.turnover}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Insight Card ────────────────────────────────────────────────────────────

function InsightCard({ icon: Icon, title, color, recent, season, label, suffix = '', window: w }) {
  const diff = (parseFloat(recent) - parseFloat(season)).toFixed(1);
  const isUp = parseFloat(diff) > 0;
  const isDown = parseFloat(diff) < 0;

  let message;
  if (isUp)        message = `${label} is up ${diff}${suffix} over the last ${w} games vs season avg.`;
  else if (isDown) message = `${label} is down ${Math.abs(diff)}${suffix} over the last ${w} games vs season avg.`;
  else             message = `${label} is right at the season average over the last ${w} games.`;

  return (
    <div className={`${colorMap[color].bg} border ${colorMap[color].border} rounded-2xl p-5`}>
      <div className="flex items-center gap-3 mb-3">
        <Icon className={`h-5 w-5 ${colorMap[color].text}`} />
        <h5 className="text-white font-bold">{title}</h5>
      </div>
      <div className={`text-3xl font-bold ${colorMap[color].text} mb-1`}>{recent}{suffix}</div>
      <p className="text-gray-400 text-sm">{message}</p>
    </div>
  );
}

// ─── Betting Analytics ───────────────────────────────────────────────────────
// Uses the player's current-season game logs to build a normal distribution
// for each stat, then computes P(X > threshold) via CDF and converts to
// American odds. All math is self-contained — no extra dependencies.

// Abramowitz & Stegun approximation of the normal CDF (error < 7.5e-8)
function normalCDF(z) {
  const sign = z >= 0 ? 1 : -1;
  const x = Math.abs(z);
  const t = 1 / (1 + 0.2316419 * x);
  const d = 0.3989422820 * Math.exp(-0.5 * x * x);
  const poly = t * (0.3193815 + t * (-0.3565638 + t * (1.7814779 + t * (-1.8212560 + t * 1.3302744))));
  return 0.5 + sign * (0.5 - d * poly);
}

// P(X > threshold) given normal dist with mean μ and std dev σ
function probOver(threshold, mu, sigma) {
  if (sigma <= 0) return threshold < mu ? 1.0 : 0.0;
  return 1 - normalCDF((threshold - mu) / sigma);
}

// Convert implied probability to American odds string
function toAmericanOdds(p) {
  if (p <= 0 || p >= 1) return 'N/A';
  if (p >= 0.5) {
    return `-${Math.round((p / (1 - p)) * 100)}`;
  }
  return `+${Math.round(((1 - p) / p) * 100)}`;
}

// Colour-code an odds cell: green = likely (favourite), red = unlikely (underdog)
function oddsClass(p) {
  if (p >= 0.70) return 'text-green-400 font-semibold';
  if (p >= 0.55) return 'text-emerald-400';
  if (p >= 0.45) return 'text-yellow-300';
  if (p >= 0.30) return 'text-orange-400';
  return 'text-red-400';
}

// Extract numeric values from game logs for a given stat key
function extractValues(gameLogs, key) {
  return gameLogs
    .map((g) => {
      const v = g[key];
      return v != null ? Number(v) : null;
    })
    .filter((v) => v !== null && !isNaN(v));
}

// Compute mean + std dev from an array of numbers
function stats(values) {
  if (values.length < 2) return { mu: values[0] ?? 0, sigma: 0 };
  const mu = values.reduce((s, v) => s + v, 0) / values.length;
  const variance = values.reduce((s, v) => s + (v - mu) ** 2, 0) / values.length;
  return { mu, sigma: Math.sqrt(variance) };
}

const BETTING_CATEGORIES = [
  {
    id: 'pts',
    label: 'Points',
    emoji: '🏀',
    color: 'orange',
    logKey: 'pts',
    lines: [5, 10, 15, 20, 25, 30],
  },
  {
    id: 'reb',
    label: 'Rebounds',
    emoji: '💪',
    color: 'blue',
    logKey: 'reb',
    lines: [4, 6, 8, 10, 12],
  },
  {
    id: 'ast',
    label: 'Assists',
    emoji: '🎯',
    color: 'green',
    logKey: 'ast',
    lines: [4, 6, 8, 10, 12],
  },
  {
    id: 'fg3m',
    label: '3-Pointers Made',
    emoji: '3️⃣',
    color: 'yellow',
    logKey: 'fg3m',
    lines: [0.5, 1, 2, 3, 4, 5],
  },
  {
    id: 'blk',
    label: 'Blocks',
    emoji: '🛡️',
    color: 'red',
    logKey: 'blk',
    lines: [0.5, 1, 2, 3],
  },
  {
    id: 'stl',
    label: 'Steals',
    emoji: '🤚',
    color: 'cyan',
    logKey: 'stl',
    lines: [0.5, 1, 2, 3],
  },
];

function BettingAnalytics({ gameLogs, playerName }) {
  const [activeCategory, setActiveCategory] = useState('pts');

  // Only use games where the player actually played
  const activeLogs = useMemo(
    () => gameLogs.filter((g) => parseInt(g.min) > 0),
    [gameLogs]
  );

  const category = BETTING_CATEGORIES.find((c) => c.id === activeCategory);

  // Compute distribution for the active category
  const dist = useMemo(() => {
    if (!category) return null;
    const values = extractValues(activeLogs, category.logKey);
    if (values.length < 3) return null;
    const { mu, sigma } = stats(values);
    return { mu, sigma, n: values.length };
  }, [activeLogs, category]);

  const colors = colorMap[category?.color ?? 'orange'];

  return (
    <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700/50">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <DollarSign className="h-6 w-6 text-green-400" />
        <h4 className="text-xl font-bold text-white">🤑 Betting Analytics</h4>
        <span className="text-gray-500 text-sm ml-auto">
          Based on {activeLogs.length} games this season
        </span>
      </div>

      <p className="text-gray-500 text-xs mb-4">
        Implied odds derived from {playerName}'s current-season game-log distribution (normal approximation).
        These are statistical estimates — not sportsbook lines. For entertainment purposes.
      </p>

      {/* Category toggles */}
      <div className="flex flex-wrap gap-2 mb-5">
        {BETTING_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
              activeCategory === cat.id
                ? 'bg-green-500 text-white'
                : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
            }`}
          >
            {cat.emoji} {cat.label}
          </button>
        ))}
      </div>

      {/* Distribution summary */}
      {dist && (
        <div className={`${colors.bg} border ${colors.border} rounded-xl p-4 mb-5`}>
          <div className="flex flex-wrap gap-6">
            <div>
              <div className={`text-2xl font-bold ${colors.text}`}>
                {dist.mu.toFixed(1)}
              </div>
              <div className="text-gray-400 text-xs mt-0.5">Season avg</div>
            </div>
            <div>
              <div className={`text-2xl font-bold ${colors.text}`}>
                ±{dist.sigma.toFixed(1)}
              </div>
              <div className="text-gray-400 text-xs mt-0.5">Std dev</div>
            </div>
            <div>
              <div className={`text-2xl font-bold ${colors.text}`}>{dist.n}</div>
              <div className="text-gray-400 text-xs mt-0.5">Games sampled</div>
            </div>
          </div>
        </div>
      )}

      {/* Odds table */}
      {dist ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-600/50">
                <th className="text-left py-2 text-gray-400 font-medium">Line (Over)</th>
                <th className="text-center py-2 text-gray-400 font-medium">Implied Prob</th>
                <th className="text-center py-2 text-gray-400 font-medium">American Odds</th>
                <th className="text-center py-2 text-gray-400 font-medium">Hit rate (season)</th>
              </tr>
            </thead>
            <tbody>
              {category.lines.map((line) => {
                const p = probOver(line, dist.mu, dist.sigma);
                const odds = toAmericanOdds(p);
                const cls = oddsClass(p);
                // Actual hit rate from game log
                const values = extractValues(activeLogs, category.logKey);
                const hits = values.filter((v) => v > line).length;
                const hitRate = values.length > 0 ? ((hits / values.length) * 100).toFixed(0) : '—';
                return (
                  <tr key={line} className="border-b border-gray-700/30 hover:bg-gray-700/20 transition-colors">
                    <td className="py-3 text-white font-medium">
                      {category.emoji} {category.label} O {line}.5
                    </td>
                    <td className={`text-center py-3 ${cls}`}>
                      {(p * 100).toFixed(1)}%
                    </td>
                    <td className={`text-center py-3 text-lg font-bold ${cls}`}>
                      {odds}
                    </td>
                    <td className="text-center py-3 text-gray-300">
                      {hitRate}% ({hits}/{values.length})
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-400 text-center py-6 text-sm">
          Not enough game log data to compute odds for this stat.
        </p>
      )}

      <p className="text-gray-600 text-xs mt-4">
        * Odds use a normal distribution fitted to this player's season game log.
        Actual sportsbook lines incorporate additional factors (injuries, matchups, etc.).
      </p>
    </div>
  );
}

export default PlayerPerformance;
